import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Section, Cell, Input, Textarea, Select, Button, Spinner, Caption,
} from "@telegram-apps/telegram-ui";
import { api } from "../../lib/api.js";
import { usePageBack } from "../../lib/telegram.js";

const PAGE_SIZE = 100;

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ru-RU"); } catch { return iso; }
}

function targetLabel(w) {
  const name = [w.target_first_name, w.target_last_name].filter(Boolean).join(" ");
  if (name) return w.target_username ? `${name} (@${w.target_username})` : name;
  if (w.target_username) return `@${w.target_username}`;
  return `#${w.target_user_id}`;
}

function issuerLabel(w) {
  const name = [w.issuer_first_name, w.issuer_last_name].filter(Boolean).join(" ");
  if (name) return w.issuer_username ? `${name} (@${w.issuer_username})` : name;
  if (w.issuer_username) return `@${w.issuer_username}`;
  return w.issued_by_user_id ? `#${w.issued_by_user_id}` : "система";
}

export default function WarningsList() {
  const navigate = useNavigate();
  usePageBack("/superadmin");
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const offset = Number(params.get("offset") || 0);

  const [draft, setDraft] = useState(q);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  // issue form
  const [airfields, setAirfields] = useState([]);
  const [newSlug, setNewSlug] = useState("");
  const [newTargetId, setNewTargetId] = useState("");
  const [newReason, setNewReason] = useState("");
  const [issuing, setIssuing] = useState(false);

  function reload() {
    setData(null);
    const qs = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (q) qs.set("q", q);
    api(`/api/v1/superadmin/warnings?${qs}`).then(setData).catch((e) => setError(e.message));
  }
  useEffect(reload, [q, offset]);

  useEffect(() => {
    api("/api/v1/superadmin/airfields").then((r) => setAirfields(r.airfields)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const next = new URLSearchParams();
    if (draft) next.set("q", draft);
    setParams(next, { replace: true });
  };

  async function issue() {
    const userId = Number(newTargetId);
    if (!newSlug) { setError("Выберите аэродром."); return; }
    if (!Number.isInteger(userId) || userId <= 0) { setError("Введите users.id пилота."); return; }
    if (!newReason.trim()) { setError("Причина обязательна."); return; }
    setIssuing(true);
    setError(null);
    try {
      await api("/api/v1/superadmin/warnings", {
        method: "POST",
        body: JSON.stringify({ slug: newSlug, target_user_id: userId, reason: newReason }),
      });
      setNewSlug(""); setNewTargetId(""); setNewReason("");
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setIssuing(false);
    }
  }

  async function saveEdit() {
    if (!editing) return;
    try {
      await api(`/api/v1/superadmin/warnings/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify({ reason: editing.reason }),
      });
      setEditing(null);
      reload();
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <>
      <Section header="Выписать предупреждение">
        <div style={{ padding: "0 16px", display: "grid", gap: 8 }}>
          <Select value={newSlug} onChange={(e) => setNewSlug(e.target.value)} header="Аэродром">
            <option value="">— выберите —</option>
            {airfields.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
          </Select>
          <Input
            placeholder="users.id пилота"
            header="Пилот (users.id)"
            value={newTargetId}
            onChange={(e) => setNewTargetId(e.target.value)}
          />
          <Textarea
            placeholder="Причина (что нарушил/не явился и т.п.)"
            header="Причина"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            rows={3}
          />
          <Button onClick={issue} disabled={issuing} stretched>
            {issuing ? "Выписываем..." : "Выписать"}
          </Button>
        </div>
      </Section>

      <Section header="Поиск">
        <form onSubmit={submitSearch} style={{ padding: "0 16px 12px" }}>
          <Input
            placeholder="Текст причины, имя или @username"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={submitSearch}
          />
        </form>
      </Section>

      {error && <Section><Cell multiline>Ошибка: {error}</Cell></Section>}

      <Section header={data ? `Всего на странице: ${data.warnings.length}` : "Загрузка..."}>
        {!data && !error && <Cell><Spinner size="m" /></Cell>}
        {data?.warnings?.map((w) => (
          <Cell
            key={w.id}
            multiline
            subtitle={`${w.airfield_name} · ${formatDate(w.created_at)}${w.edited_at ? " (правка)" : ""} · от ${issuerLabel(w)}`}
          >
            <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/superadmin/users/${w.target_user_id}`); }}>
              {targetLabel(w)}
            </a>
            <br />
            {editing?.id === w.id ? (
              <div style={{ marginTop: 8 }}>
                <Textarea
                  value={editing.reason}
                  onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                  rows={3}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <Button onClick={saveEdit} size="s">Сохранить</Button>
                  <Button mode="plain" onClick={() => setEditing(null)} size="s">Отмена</Button>
                </div>
              </div>
            ) : (
              <>
                {w.reason}
                <Button
                  mode="plain"
                  size="s"
                  onClick={() => setEditing({ id: w.id, reason: w.reason })}
                  style={{ marginLeft: 8 }}
                >✏</Button>
              </>
            )}
          </Cell>
        ))}
        {data && data.warnings.length === 0 && (
          <Cell><Caption level="1">Предупреждений нет.</Caption></Cell>
        )}
      </Section>

      <div className="portal-pager">
        <button
          type="button"
          className="portal-pager__btn"
          disabled={offset === 0}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.set("offset", String(Math.max(0, offset - PAGE_SIZE)));
            setParams(next, { replace: true });
          }}
        >← Назад</button>
        <span>записей: {data?.warnings.length ?? 0}</span>
        <button
          type="button"
          className="portal-pager__btn"
          disabled={!data || data.warnings.length < PAGE_SIZE}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.set("offset", String(offset + PAGE_SIZE));
            setParams(next, { replace: true });
          }}
        >Дальше →</button>
      </div>
    </>
  );
}
